import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { RefreshCw, Search, X } from 'lucide-react'
import { fetchAccountsBalances } from '../../services/api'
import Loader from '../../components/Loader'
import Pagination from '../../components/Pagination'
import {
	Alert,
	Badge,
	Button,
	Card,
	EmptyState,
	PageHeader,
	Select,
	TextInput,
	formatCurrency,
	statusTone,
} from '../../components/ui'

const SPECIAL_ACCOUNT_TYPES = [
	{ value: '', label: 'Todos' },
	{ value: 'STANDARD_ACCOUNT', label: 'Normal' },
	{ value: 'PAYING_AGENT', label: 'Pagadoria' },
]

const PER_PAGE_OPTIONS = [
	{ value: '10', label: '10 por página' },
	{ value: '20', label: '20 por página' },
	{ value: '50', label: '50 por página' },
]

const SITUATION_FILTERS = [
	{ value: '', label: 'Todas' },
	{ value: 'DIVERGENT', label: 'Somente divergentes' },
	{ value: 'ERROR', label: 'Somente com erro na consulta' },
]

const specialAccountTypeLabel = (value) =>
	SPECIAL_ACCOUNT_TYPES.find((option) => option.value === value)?.label || '—'

const emptyFilters = { number: '', accountHolderName: '', specialType: '' }

// Compara em centavos para não acusar divergência por arredondamento de ponto flutuante.
const toCents = (value) => Math.round(Number(value) * 100)

const reconcile = (account) => {
	if (account.qitechConsultError) {
		return { situation: 'ERROR', difference: null }
	}
	if (account.qitechBalance === null || account.qitechBalance === undefined) {
		return { situation: 'ERROR', difference: null }
	}
	const local = account.balance ?? 0
	const differenceCents = toCents(account.qitechBalance) - toCents(local)
	return {
		situation: differenceCents === 0 ? 'MATCH' : 'DIVERGENT',
		difference: differenceCents / 100,
	}
}

const SITUATION_BADGE = {
	MATCH: { tone: 'green', label: 'Conferido' },
	DIVERGENT: { tone: 'red', label: 'Divergente' },
	ERROR: { tone: 'yellow', label: 'Erro na consulta' },
}

const SummaryTile = ({ label, value, tone = 'text-gray-900' }) => (
	<div className="rounded-lg bg-white px-4 py-3 shadow">
		<p className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</p>
		<p className={`mt-1 text-2xl font-semibold ${tone}`}>{value}</p>
	</div>
)

const AccountsBalances = () => {
	const navigate = useNavigate()
	const [filters, setFilters] = useState(emptyFilters)
	const [appliedFilters, setAppliedFilters] = useState(emptyFilters)
	const [page, setPage] = useState(1)
	const [perPage, setPerPage] = useState('10')
	const [situationFilter, setSituationFilter] = useState('')
	const [data, setData] = useState(null)
	const [isLoading, setIsLoading] = useState(true)
	const [error, setError] = useState('')
	const [consultedAt, setConsultedAt] = useState(null)

	const load = useCallback(async () => {
		setIsLoading(true)
		setError('')
		try {
			const response = await fetchAccountsBalances({
				...appliedFilters,
				page,
				perPage,
				orderColumn: 'id',
				orderDirection: 'ASC',
			})
			setData(response)
			setConsultedAt(new Date())
		} catch (err) {
			setError(err.message)
			setData(null)
		} finally {
			setIsLoading(false)
		}
	}, [appliedFilters, page, perPage])

	useEffect(() => {
		load()
	}, [load])

	const handleFilterChange = (e) => {
		const { name, value } = e.target
		setFilters((prev) => ({ ...prev, [name]: value }))
	}

	const handleSubmit = (e) => {
		e.preventDefault()
		setPage(1)
		setAppliedFilters(filters)
	}

	const handleClear = () => {
		setFilters(emptyFilters)
		setAppliedFilters(emptyFilters)
		setSituationFilter('')
		setPage(1)
	}

	const rows = useMemo(
		() => (data?.content || []).map((account) => ({ ...account, ...reconcile(account) })),
		[data],
	)

	const visibleRows = situationFilter ? rows.filter((row) => row.situation === situationFilter) : rows

	const summary = useMemo(() => {
		const count = (situation) => rows.filter((row) => row.situation === situation).length
		const totalDifference = rows.reduce((sum, row) => sum + toCents(row.difference ?? 0), 0) / 100
		return {
			matched: count('MATCH'),
			divergent: count('DIVERGENT'),
			errors: count('ERROR'),
			totalDifference,
		}
	}, [rows])

	return (
		<div>
			<PageHeader
				title="Saldos x QITech"
				subtitle="GET /backoffice/accounts/balances — compara o saldo registrado no KSI Pay com o saldo consultado na QITech"
				actions={
					<Button variant="secondary" onClick={load} disabled={isLoading}>
						<RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
						Consultar novamente
					</Button>
				}
			/>

			<Card className="mb-6">
				<form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6">
					<TextInput
						id="number"
						name="number"
						label="Numero da conta"
						placeholder="Ex.: 123456"
						value={filters.number}
						onChange={handleFilterChange}
					/>
					<TextInput
						id="accountHolderName"
						name="accountHolderName"
						label="Titular"
						placeholder="Nome do titular"
						value={filters.accountHolderName}
						onChange={handleFilterChange}
					/>
					<Select
						id="specialType"
						name="specialType"
						label="Tipo de conta"
						options={SPECIAL_ACCOUNT_TYPES}
						value={filters.specialType}
						onChange={handleFilterChange}
					/>
					<Select
						id="perPage"
						name="perPage"
						label="Contas por consulta"
						options={PER_PAGE_OPTIONS}
						value={perPage}
						onChange={(e) => {
							setPage(1)
							setPerPage(e.target.value)
						}}
					/>
					<div className="flex items-end gap-2 lg:col-span-2">
						<Button type="submit">
							<Search className="h-4 w-4" />
							Buscar
						</Button>
						<Button type="button" variant="secondary" onClick={handleClear}>
							<X className="h-4 w-4" />
							Limpar
						</Button>
					</div>
				</form>
				<p className="mt-3 text-xs text-gray-500">
					Cada busca consulta a QITech conta a conta, em tempo real — páginas maiores demoram mais.
				</p>
			</Card>

			<Alert onDismiss={() => setError('')}>{error}</Alert>

			{!isLoading && rows.length > 0 && (
				<div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
					<SummaryTile label="Conferidas" value={summary.matched} tone="text-green-700" />
					<SummaryTile label="Divergentes" value={summary.divergent} tone="text-red-700" />
					<SummaryTile label="Erro na consulta" value={summary.errors} tone="text-yellow-700" />
					<SummaryTile
						label="Diferença total (QITech − KSI Pay)"
						value={formatCurrency(summary.totalDifference)}
						tone={summary.totalDifference === 0 ? 'text-gray-900' : 'text-red-700'}
					/>
				</div>
			)}

			<div className="overflow-hidden rounded-lg bg-white shadow">
				<div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-3">
					<p className="text-sm text-gray-500">
						{consultedAt
							? `Consultado às ${consultedAt.toLocaleTimeString('pt-BR')} · resumo desta página`
							: 'Aguardando consulta'}
					</p>
					<Select
						id="situationFilter"
						name="situationFilter"
						options={SITUATION_FILTERS}
						value={situationFilter}
						onChange={(e) => setSituationFilter(e.target.value)}
					/>
				</div>

				{isLoading ? (
					<div className="flex h-64 flex-col items-center justify-center gap-3">
						<Loader size="lg" />
						<p className="text-sm text-gray-500">Consultando saldos na QITech…</p>
					</div>
				) : visibleRows.length === 0 ? (
					<EmptyState
						message={rows.length === 0 ? 'Nenhuma conta encontrada' : 'Nenhuma conta nesta situação nesta página'}
					/>
				) : (
					<>
						<div className="overflow-x-auto">
							<table className="min-w-full divide-y divide-gray-200">
								<thead className="bg-gray-50">
									<tr>
										<th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
											Numero
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
											Titular
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
											Tipo / Status
										</th>
										<th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
											Saldo KSI Pay
										</th>
										<th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
											Saldo QITech
										</th>
										<th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
											Diferença
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
											Situação
										</th>
										<th className="px-6 py-3" />
									</tr>
								</thead>
								<tbody className="divide-y divide-gray-200 bg-white">
									{visibleRows.map((account) => {
										const badge = SITUATION_BADGE[account.situation]
										return (
											<tr
												key={account.id}
												className={account.situation === 'DIVERGENT' ? 'bg-red-50/40 hover:bg-red-50' : 'hover:bg-gray-50'}
											>
												<td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
													{account.number || '—'}
												</td>
												<td className="px-6 py-4 text-sm text-gray-900">
													{account.accountHolder?.name || '—'}
												</td>
												<td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
													<div>{specialAccountTypeLabel(account.specialType)}</div>
													<div className="mt-1">
														<Badge tone={statusTone(account.status)}>{account.status || '—'}</Badge>
													</div>
												</td>
												<td className="whitespace-nowrap px-6 py-4 text-right text-sm tabular-nums text-gray-900">
													{formatCurrency(account.balance)}
												</td>
												<td className="whitespace-nowrap px-6 py-4 text-right text-sm tabular-nums text-gray-900">
													{formatCurrency(account.qitechBalance)}
												</td>
												<td
													className={`whitespace-nowrap px-6 py-4 text-right text-sm font-medium tabular-nums ${
														account.situation === 'DIVERGENT' ? 'text-red-700' : 'text-gray-500'
													}`}
												>
													{account.difference === null ? '—' : formatCurrency(account.difference)}
												</td>
												<td className="px-6 py-4 text-sm">
													<Badge tone={badge.tone}>{badge.label}</Badge>
													{account.qitechConsultError && (
														<p className="mt-1 max-w-xs text-xs text-yellow-800">{account.qitechConsultError}</p>
													)}
												</td>
												<td className="whitespace-nowrap px-6 py-4 text-right">
													<Button variant="secondary" onClick={() => navigate(`/contas/${account.id}`)}>
														Detalhes
													</Button>
												</td>
											</tr>
										)
									})}
								</tbody>
							</table>
						</div>

						<Pagination
							currentPage={data?.currentPage || page}
							totalPages={data?.totalPages || 1}
							onPageChange={setPage}
						/>
					</>
				)}
			</div>
		</div>
	)
}

export default AccountsBalances
